// @file Plans store
import { SubscriptionFilter } from '@@/bits/landside_helper'
import type { PlanPricing, SubscriptionPlan } from '@@/bits/plans_helper'
import {
  accountSetupBusinessSubscriptions,
  accountSetupPersonalSubscriptions,
  accountSetupStudentSubscriptions,
  accountSetupTeacherAndStaffSubscriptions,
  businessSubscriptions,
  educationSubscriptions,
  freeSubscription,
  homeSubscriptions,
  libraryCreateClassroomSubscriptions,
  libraryCreateTeamSubscriptions,
  SubscriptionType,
  updateSubscriptionsWithPrice,
} from '@@/bits/plans_helper'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

export const usePlansStore = defineStore('plans', () => {
  const homePlanPrices = ref<PlanPricing[]>([])
  const businessPlanPrices = ref<PlanPricing[]>([])
  const classroomPlanPrices = ref<PlanPricing[]>([])
  const currencySign = ref('')

  // Getters

  const freePlans = computed(() => freeSubscription)

  const homePlans = computed((): SubscriptionPlan[] =>
    updateSubscriptionsWithPrice(homeSubscriptions, homePlanPrices.value, currencySign.value),
  )

  const businessPlans = computed((): SubscriptionPlan[] =>
    updateSubscriptionsWithPrice(businessSubscriptions, businessPlanPrices.value, currencySign.value),
  )

  const educationPlans = computed((): SubscriptionPlan[] => {
    const plans = educationSubscriptions

    // TODO PRO-9147: Replace hardcoded backpack prices with prices from the backend
    const classroomPlan = plans.filter((plan) => plan.subType === SubscriptionType.Classroom)
    const updatedClassroomPlan = updateSubscriptionsWithPrice(
      classroomPlan,
      classroomPlanPrices.value,
      currencySign.value,
    )
    const schoolPlan = plans.filter((plan) => plan.subType === SubscriptionType.School)
    return [...updatedClassroomPlan, ...schoolPlan]
  })

  const allPlans = computed((): SubscriptionPlan[] => [
    ...freePlans.value,
    ...homePlans.value,
    ...businessPlans.value,
    ...educationPlans.value,
  ])

  const libraryCreateClassroomPlans = computed((): SubscriptionPlan[] =>
    updateSubscriptionsWithPrice(libraryCreateClassroomSubscriptions, classroomPlanPrices.value, currencySign.value),
  )
  const libraryCreateTeamPlans = computed((): SubscriptionPlan[] =>
    updateSubscriptionsWithPrice(libraryCreateTeamSubscriptions, businessPlanPrices.value, currencySign.value),
  )

  const accountSetupPersonalPlans = computed((): SubscriptionPlan[] => {
    const freePlan = accountSetupPersonalSubscriptions.find(
      (plan) => plan.subType === SubscriptionType.Free,
    ) as SubscriptionPlan
    const homePlans = accountSetupPersonalSubscriptions.filter((plan) => plan.filterType === SubscriptionFilter.HOME)
    const businessPlans = accountSetupPersonalSubscriptions.filter(
      (plan) => plan.filterType === SubscriptionFilter.BUSINESS,
    )

    const updatedHomePlans = updateSubscriptionsWithPrice(homePlans, homePlanPrices.value, currencySign.value)
    const updatedBusinessPlans = updateSubscriptionsWithPrice(
      businessPlans,
      businessPlanPrices.value,
      currencySign.value,
    )

    return [freePlan, ...updatedHomePlans, ...updatedBusinessPlans]
  })

  const accountSetupStudentPlans = computed((): SubscriptionPlan[] => {
    return updateSubscriptionsWithPrice(accountSetupStudentSubscriptions, homePlanPrices.value, currencySign.value)
  })

  const accountSetupTeacherAndStaffPlans = computed((): SubscriptionPlan[] => {
    const freePlan = accountSetupTeacherAndStaffSubscriptions.find(
      (plan) => plan.subType === SubscriptionType.Free,
    ) as SubscriptionPlan
    const homePlans = accountSetupTeacherAndStaffSubscriptions.filter(
      (plan) => plan.filterType === SubscriptionFilter.HOME,
    )
    const classroomPlans = accountSetupTeacherAndStaffSubscriptions.filter(
      (plan) => plan.subType === SubscriptionType.Classroom,
    )
    const businessPlans = accountSetupTeacherAndStaffSubscriptions.filter(
      (plan) => plan.filterType === SubscriptionFilter.BUSINESS,
    )

    const updatedHomePlans = updateSubscriptionsWithPrice(homePlans, homePlanPrices.value, currencySign.value)
    const updatedClassroomPlans = updateSubscriptionsWithPrice(
      classroomPlans,
      classroomPlanPrices.value,
      currencySign.value,
    )
    const updatedBusinessPlans = updateSubscriptionsWithPrice(
      businessPlans,
      businessPlanPrices.value,
      currencySign.value,
    )

    return [freePlan, ...updatedHomePlans, ...updatedClassroomPlans, ...updatedBusinessPlans]
  })

  const accountSetupBusinessPlans = computed((): SubscriptionPlan[] => {
    const freePlan = accountSetupBusinessSubscriptions.find(
      (plan) => plan.subType === SubscriptionType.Free,
    ) as SubscriptionPlan
    const homePlans = accountSetupBusinessSubscriptions.filter((plan) => plan.filterType === SubscriptionFilter.HOME)
    const businessPlans = accountSetupBusinessSubscriptions.filter(
      (plan) => plan.filterType === SubscriptionFilter.BUSINESS,
    )
    const updatedHomePlans = updateSubscriptionsWithPrice(homePlans, homePlanPrices.value, currencySign.value)
    const updatedBusinessPlans = updateSubscriptionsWithPrice(
      businessPlans,
      businessPlanPrices.value,
      currencySign.value,
    )

    return [freePlan, ...updatedHomePlans, ...updatedBusinessPlans]
  })

  // Actions

  function initializeState(data: {
    homePlans: PlanPricing[]
    teamPlans: PlanPricing[]
    classroomPlans: PlanPricing[]
    currencySign: string
  }): void {
    currencySign.value = data.currencySign ?? currencySign.value
    homePlanPrices.value = data.homePlans ?? homePlanPrices.value
    businessPlanPrices.value = data.teamPlans ?? businessPlanPrices.value
    classroomPlanPrices.value = data.classroomPlans ?? classroomPlanPrices.value
  }

  return {
    // State
    currencySign,

    // Getters
    freePlans,
    homePlans,
    businessPlans,
    educationPlans,
    allPlans,
    libraryCreateClassroomPlans,
    libraryCreateTeamPlans,
    accountSetupPersonalPlans,
    accountSetupStudentPlans,
    accountSetupTeacherAndStaffPlans,
    accountSetupBusinessPlans,

    // Actions
    initializeState,
  }
})
